import { useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Field, Icon, Input, useToast } from "@xinghai/ui";
import { accountApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { homeFor, primaryRole } from "../../app/roles";
import { confirmIssue, passwordHint, passwordIssues } from "../auth/passwordRules";

/** ACCOUNTS.md §3 — 改密码, and the only page reachable while must_change_password. */
export function PasswordPage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const forced = me?.must_change_password === true;
  const username = me?.user.username ?? "";

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => accountApi.changePassword(current, next),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: qk.sessions });
      toast.success("密码已经更新。");
      setCurrent("");
      setNext("");
      setConfirm("");
      if (forced && me) void navigate(homeFor(primaryRole(me.user.roles)), { replace: true });
    },
    onError: (err) => {
      setError(
        friendlyMessage(err, "没能修改密码，请重试。", {
          BAD_CREDENTIALS: "当前密码不对，请再试一次。",
          WEAK_PASSWORD: "新密码太弱了：至少 10 位，且不要包含用户名。",
        }),
      );
    },
  });

  const issues = next ? passwordIssues(next, username) : [];
  const mismatch = confirm ? confirmIssue(next, confirm) : null;
  const hint = passwordHint(next, username);

  return (
    <div className="xz-container xz-container--readable xz-stack">
      <h1 className="gz-type-heading">修改密码</h1>

      {forced && (
        <p className="gz-notice" role="status">
          <Icon id="ui-info" size={20} />
          你现在用的是临时密码。设一个只有你知道的新密码之后才能继续使用。
        </p>
      )}

      <Card padding="md">
        <form
          className="xz-stack"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (current.length === 0) return setError("请先填写当前密码。");
            if (next.length === 0 || issues.length > 0 || mismatch) {
              return setError("请先按提示修好新密码。");
            }
            change.mutate();
          }}
        >
          <Field label="当前密码">
            <Input
              type="password"
              value={current}
              autoComplete="current-password"
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
          <Field
            label="新密码"
            hint={next ? `${hint.label}：${hint.advice}` : "至少 10 位，不要包含用户名"}
            error={issues[0]}
          >
            <Input
              type="password"
              value={next}
              autoComplete="new-password"
              onChange={(e) => setNext(e.target.value)}
            />
          </Field>
          <Field label="再输一次新密码" error={mismatch ?? undefined}>
            <Input
              type="password"
              value={confirm}
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
            />
          </Field>

          {error && (
            <p className="gz-notice gz-notice--error" role="alert">
              <Icon id="ui-error" size={20} />
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" block loading={change.isPending}>
            保存新密码
          </Button>
        </form>
      </Card>
    </div>
  );
}
